/**
 * useCookieConsent - Cookie consent state management
 *
 * v2.0 - GDPR-03: granular consent schema, migration from v1.0 - 2026-03-17
 * v1.0 - GDPR-02: Cookie consent banner localStorage hook - 2026-03-15
 */
import { useState, useEffect } from 'react';

const CONSENT_KEY = 'mylojic_cookie_consent';
const CONSENT_VERSION = '2.0';
const EXPIRY_DAYS = 365;

// v2.0 schema
interface ConsentRecord {
  version: string;
  timestamp: string;
  categories: {
    essential: boolean;
    analytics: boolean;
  };
}

// v1.0 schema (for migration only)
interface LegacyConsentRecord {
  accepted: boolean;
  timestamp: string;
  version: string;
}

export interface UseCookieConsentReturn {
  showBanner: boolean;
  analyticsEnabled: boolean;
  acceptAll: () => void;
  acceptEssentialOnly: () => void;
  savePreferences: (analytics: boolean) => void;
}

export const useCookieConsent = (): UseCookieConsentReturn => {
  const [showBanner, setShowBanner] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(CONSENT_KEY);
      if (!raw) {
        setShowBanner(true);
        return;
      }

      const parsed = JSON.parse(raw);

      // Migration: v1.0 had `accepted` boolean at top level
      if (parsed.version !== CONSENT_VERSION) {
        const legacy = parsed as LegacyConsentRecord;
        if (legacy.version === '1.0' && typeof legacy.accepted === 'boolean') {
          // Migrate v1.0 → v2.0 silently (user already gave consent)
          const migrated: ConsentRecord = {
            version: CONSENT_VERSION,
            timestamp: legacy.timestamp || new Date().toISOString(),
            categories: {
              essential: true,
              analytics: legacy.accepted,
            },
          };
          localStorage.setItem(CONSENT_KEY, JSON.stringify(migrated));
          setAnalyticsEnabled(legacy.accepted);
          setShowBanner(false);
          return;
        }
        // Unknown version — show banner again
        setShowBanner(true);
        return;
      }

      const record = parsed as ConsentRecord;

      // Re-show if consent older than 12 months
      const consentDate = new Date(record.timestamp);
      const expiryDate = new Date(consentDate);
      expiryDate.setDate(expiryDate.getDate() + EXPIRY_DAYS);
      if (new Date() > expiryDate) {
        setShowBanner(true);
        return;
      }

      // Valid v2.0 consent — restore state
      setShowBanner(false);
      setAnalyticsEnabled(record.categories?.analytics ?? false);
    } catch {
      // Corrupt record — show banner again
      setShowBanner(true);
    }
  }, []);

  const writeConsent = (analytics: boolean) => {
    const record: ConsentRecord = {
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
      categories: {
        essential: true,
        analytics,
      },
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
    setAnalyticsEnabled(analytics);
    setShowBanner(false);
  };

  const acceptAll = () => writeConsent(true);
  const acceptEssentialOnly = () => writeConsent(false);
  const savePreferences = (analytics: boolean) => writeConsent(analytics);

  return { showBanner, analyticsEnabled, acceptAll, acceptEssentialOnly, savePreferences };
};
